import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from 'api';
import NumberedDataGridPagination from 'components/NumberedDataGridPagination';

const ReportsDialog = ({ handleClose, handleNewReport }) => {
    const navigate = useNavigate();
    const visitID = useSelector((state) => state.chart.visit_id);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);

    const columns = useMemo(
        () => [
            { field: 'urpt_name', headerName: 'Report Name', flex: 1, minWidth: 180, sortable: false },
            { field: 'date_created', headerName: 'Date Created', flex: 1, minWidth: 160, sortable: false },
            { field: 'template_name', headerName: 'Template', flex: 1, minWidth: 200, sortable: false },
        ],
        []
    );

    useEffect(() => {
        if (!visitID) {
            setRows([]);
            setTotal(0);
            return;
        }

        setLoading(true);
        api.get('/reports/chart', {
            params: {
                page_size: pageSize,
                page_number: page,
                visit_id: visitID,
            },
        })
            .then((res) => {
                const { reports } = res.data;
                setRows(reports || []);
                setTotal(reports[0]?.total_records || 0);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [visitID, page, pageSize]);

    const handleSelection = (selection) => {
        const rowId = selection[0];
        setSelectedRowId(rowId);
        const row = rows.find((item) => item.urpt_id === rowId);
        if (row) {
            navigate({ to: `/reports/edit/${row.urpt_id}` });
        }
    };

    return (
        <Dialog fullWidth maxWidth="lg" open onClose={handleClose}>
            <DialogTitle>Reports for current chart</DialogTitle>

            <DialogContent>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={total}
                    paginationMode="server"
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    rowSelectionModel={selectedRowId ? [selectedRowId] : []}
                    onRowSelectionModelChange={(newSelection) => handleSelection(newSelection)}
                    getRowId={(row) => row.urpt_id}
                    pageSizeOptions={[10, 20, 50, 100]}
                    disableColumnMenu
                    disableCellFocus
                    slots={{ pagination: NumberedDataGridPagination }}
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                    }}
                    autoHeight
                />
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => {
                        handleClose();
                        handleNewReport();
                    }}
                    variant="contained"
                    color="primary"
                >
                    New Report
                </Button>

                <Button onClick={handleClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportsDialog;
